import React from 'react';
import AuroraBackground from '../components/AuroraBackground';
import structure from '../assets/structure.jpg'

const categories = [
  {
    name: 'Display',
    specs: [
      { value: 'Bright and visible in full daylight' },
      { value: 'Full-color and dual-color LED options' },
      { value: 'Large display area for clear messages' },
      { value: 'Fast refresh rate for smooth animations' },
      { value: 'Wide viewing angles for better visibility from all directions' },
    ],
  },
  {
    name: 'Radar Sensor',
    specs: [
      { value: 'Accurately detects vehicle speeds up to 220 km/h' },
      { value: 'Works over 100 meters away' },
      { value: 'Covers up to 4 lanes depending on model' },
      { value: 'Built-in angle adjustment for optimal targeting' },
      { value: 'Filters out pedestrians and small objects' },
      { value: 'Direction adjustment capability' },
    ],
  },
  {
    name: 'Power System',
    specs: [
      { value: 'Long-lasting smart lithium-ion battery' },
      { value: 'Solar powered for off-grid operation' },
      { value: 'High battery capacity for longer runtimes' },
      { value: 'Smart battery management system for safety' },
      { value: 'Fast and safe charging with protection features' },
    ],
  },
  {
    name: 'Solar Performance',
    specs: [
      { value: 'Solar panels sized for each panel' },
      { value: 'Works reliably even in cloudy conditions' },
      { value: 'Autonomous runtime up to 100 hours' },
      { value: 'Efficient power usage for extended operation' },
    ],
  },
  {
    name: 'Web-Based Portal',
    specs: [
      { value: 'Control signs from any location via internet' },
      { value: 'Real-time speed monitoring and logs' },
      { value: 'Schedule active times and automate contents' },
      { value: 'Group multiple signs in one view' },
      { value: 'Secure access with user roles and logs' },
      { value: 'Custom messages, images, and animation upload' },
      { value: 'Speed-based message triggers' },
      { value: 'Emergency alert broadcasting' },
      { value: 'Downloadable performance reports' },
      { value: 'Remote firmware Updates (OTA)' },
      { value: 'No software installation needed' },
    ],
  },
  {
    name: 'Connectivity & Control',
    specs: [
      { value: 'Connects via 4G, Wi-Fi, Bluetooth, and Ethernet' },
      { value: 'Real-time data sync with the portal' },
      { value: 'GPS tracking of sign location' },
      { value: 'Remote configuration and diagnostics' },
      { value: 'Control multiple signs at once' },
    ],
  },
  {
    name: 'Security Features',
    specs: [
      { value: 'GPS anti-theft tracking' },
      { value: 'Tilt and door sensors detect tampering' },
      { value: 'Built-in thermometer for thermal safety' },
      { value: 'Comprehensive battery protections' },
      { value: 'Logs all access and changes for accountability' },
    ],
  },
  {
    name: 'Compliance & Local Support',
    specs: [
      { value: 'Manufactured in Australia' },
      { value: 'Data hosted on Australian servers' },
      { value: 'Local support for service and updates' },
      { value: 'Designed to meet Australian traffic standards' },
      { value: 'Designed to meet Australian environmental conditions' },
      { value: 'Encrypted data transfer and storage' },
    ],
  },
];

const CheckMark = ({ tone = 'text-brand', fill = 'fill-brand/10' }) => (
  <svg viewBox="0 0 20 20" fill="none" className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} aria-hidden="true">
    <circle cx="10" cy="10" r="9" className={fill} />
    <path
      d="M6 10.5l2.5 2.5L14 7.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom SVG icons for professional look
const DisplayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const RadarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </svg>
);

const BatteryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <rect x="2" y="7" width="16" height="10" rx="2" />
    <path d="M20 10h2v4h-2" />
    <path d="M6 12h8" />
  </svg>
);

const SolarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const PortalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);

const ConnectivityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <path d="M5 12.55a11 11 0 0114.08 0" />
    <path d="M1.42 9a16 16 0 0121.16 0" />
    <path d="M8.83 16.42a5.5 5.5 0 016.34 0" />
    <circle cx="12" cy="20" r="1" fill="currentColor" />
  </svg>
);

const SecurityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ComplianceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const categoryIcons = {
  'Display': <DisplayIcon />,
  'Radar Sensor': <RadarIcon />,
  'Power System': <BatteryIcon />,
  'Solar Performance': <SolarIcon />,
  'Web-Based Portal': <PortalIcon />,
  'Connectivity & Control': <ConnectivityIcon />,
  'Security Features': <SecurityIcon />,
  'Compliance & Local Support': <ComplianceIcon />
};

const Features = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      <AuroraBackground />

      <div className="relative z-10 mx-auto max-w-[1580px] px-6 py-24 md:py-28">
        {/* Header */}
        <header className="mb-16 max-w-[1440px]">
          <span className="mb-4 inline-block bg-[linear-gradient(135deg,#ff3b30,#30d158,#0a84ff)] bg-clip-text text-xs font-semibold uppercase tracking-[0.25em] text-transparent">
            Everything you need
          </span>
          <h1 className="text-[2.5rem] font-extrabold leading-[1.05] tracking-[-1.5px] text-[#0f172a] md:text-[3.25rem]">
            Key Features
          </h1>
        </header>

        {/* Professional upfront cards: all content visible, subtle animations */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((category, catIndex) => (
            <div
              key={catIndex}
              className="group relative overflow-hidden rounded-xl border border-slate-100 bg-white p-7 shadow-sm transition-all duration-300 hover:border-slate-200 hover:shadow-lg"
            >
              {/* Subtle gradient accent */}
              <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-blue-500 to-green-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              
              {/* Icon + Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-50 text-slate-700 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors duration-300">
                  {categoryIcons[category.name]}
                </div>
                <h3 className="text-base font-semibold text-slate-900 tracking-tight">{category.name}</h3>
              </div>
              {/* Spec list - clean typography */}
              <ul className="space-y-2.5 text-sm text-slate-600 leading-relaxed">
                {category.specs.map((spec, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckMark tone="text-green-600" fill="fill-green-50/80" />
                    <span>{spec.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mb-16 w-full">
          <img 
            src={structure}
            alt="Australian national network structure map"
            style={{ 
              width: '70%', 
              height: 'auto',
              display: 'block',
              alignContent: 'center',
              marginTop: '2rem',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Features;